import { httpClient } from "@/shared/api";

import type { Company, LoginCredentials } from "../model/types";
import type { CompanyResponseDTO, LoginRequestDTO } from "./dto";

/**
 * `POST /api/auth/login` — публічний (OpenAPI `security: []`). Несе
 * `skipAuthRedirect`: його 401 локальний/очікуваний, глобальний 401-handler
 * (ітер.2, ADR-0006 D4) не повинен перехоплювати. Тіло відповіді — `{}`;
 * назву компанії підтягуємо окремим `fetchCompany` (ADR-0006 D2).
 */
export async function login(creds: LoginCredentials): Promise<void> {
  const body: LoginRequestDTO = {
    email: creds.email,
    password: creds.password,
    remember_me: creds.remember_me,
  };
  await httpClient.post("/auth/login", body, { skipAuthRedirect: true });
}

/**
 * `POST /api/auth/logout` — завершити сесію (CSRF-protected, OpenAPI → 204).
 * Несе `skipAuthRedirect`: logout САМ керує редіректом (widget), тож його
 * можливий 401/403 не повинен тригерити глобальний 401-handler і дублювати
 * редірект (ADR-0006 D4). `X-CSRF-Token` додасть request-interceptor (ітер.1)
 * з cookie `csrf` автоматично — вручну не виставляємо.
 */
export async function logout(): Promise<void> {
  await httpClient.post("/auth/logout", undefined, { skipAuthRedirect: true });
}

/**
 * `GET /api/company` — company поточної сесії (row-level tenancy gate, ADR-0004).
 * Мапить DTO → доменний `Company` (тут поля збігаються — межа лишається).
 *
 * `opts.skipAuthRedirect` — для eager-bootstrap (ADR-0006 D2): його 401
 * (анонім) очікуваний і НЕ повинен тригерити глобальний 401-redirect (D4).
 * login-флоу викликає без прапора (після login сесія валідна).
 */
export async function fetchCompany(opts?: {
  skipAuthRedirect?: boolean;
}): Promise<Company> {
  const { data } = await httpClient.get<CompanyResponseDTO>("/company", {
    skipAuthRedirect: opts?.skipAuthRedirect,
  });
  return { id: data.id, name: data.name, timezone: data.timezone };
}
